import { useEffect, useMemo, useState } from "react";
import { Eye, FileCheck2, Search } from "lucide-react";
import "./HRReleasingOfferLetterPage.css";
import { getApplications } from "../../services/jobService";
import { uploadOfferLetter } from "../../services/offerLetterService";

const formatAppliedDate = (value) => {
  if (!value) return "-";

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString("en-GB");
};

const HRReleasingOfferLetterPage = () => {
  const [searchText, setSearchText] = useState("");
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCandidate, setActiveCandidate] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [fileError, setFileError] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [offerLetterForm, setOfferLetterForm] = useState({
    candidateId: "",
    fullName: "",
    email: "",
    employmentType: "",
    location: "",
    dateOfJoining: "",
    workMode: "",
  });

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    window.location.reload();
  };

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  useEffect(() => {
    const fetchSelectedCandidates = async () => {
      try {
        const response = await getApplications();
        const applications = Array.isArray(response?.data) ? response.data : [];

        const normalizedCandidates = applications
          .filter(
            (item) => String(item?.status || "").toUpperCase() === "SELECTED",
          )
          .map((item) => ({
            id: item.applicationId || item.id,
            candidateId: item.candidateId || "",
            fullName: item.fullName || "Unknown Candidate",
            email: item.email || "-",
            phone: item.phone || "-",
            jobTitle: item.jobTitle || "-",
            location: item.location || "-",
            status: item.status || "SELECTED",
            createdAt: item.createdAt || "",
          }));

        setSelectedCandidates(normalizedCandidates);
      } catch (error) {
        console.error("Failed to fetch selected candidates:", error);
        setSelectedCandidates([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSelectedCandidates();
  }, []);

  const filteredCandidates = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    if (!keyword) {
      return selectedCandidates;
    }

    return selectedCandidates.filter((candidate) => {
      const searchableText = [
        candidate.fullName,
        candidate.email,
        candidate.jobTitle,
        candidate.location,
      ]
        .join(" ")
        .toLowerCase();

      return searchableText.includes(keyword);
    });
  }, [searchText, selectedCandidates]);

  const handleOpenCandidate = (candidate) => {
    setActiveCandidate(candidate);
    setSelectedFile(null);
    setSelectedFileName("");
    setFileError("");
    setIsPopupOpen(false);
    setOfferLetterForm({
      candidateId: "",
      fullName: candidate.fullName || "",
      email: candidate.email || "",
      employmentType: "",
      location: "",
      dateOfJoining: "",
      workMode: "",
    });
  };

  const handleApplicationIdChange = (event) => {
    setOfferLetterForm((prev) => ({
      ...prev,
      candidateId: event.target.value,
      fullName: activeCandidate?.fullName || "",
      email: activeCandidate?.email || "",
    }));
  };

  const handleOfferLetterFieldChange = (field, value) => {
    setOfferLetterForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      setSelectedFileName("");
      setFileError("");
      return;
    }

    const isPdfFile =
      file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

    if (!isPdfFile) {
      setSelectedFile(null);
      setSelectedFileName("");
      setFileError("Only PDF files are allowed.");
      event.target.value = "";
      return;
    }

    setSelectedFile(file);
    setSelectedFileName(file.name);
    setFileError("");
  };

  const handleSubmitOfferLetter = async (event) => {
    event.preventDefault();

    if (fileError || !selectedFile) {
      setFileError("Only PDF files are allowed.");
      return;
    }

    const candidateId = offerLetterForm.candidateId.trim();
    const applicationId = activeCandidate?.id;

    if (!offerLetterForm.candidateId.trim() || !candidateId || !applicationId) {
      setFileError("Please enter a valid candidate ID.");
      return;
    }

    try {
      setIsSubmitting(true);
      setFileError("");

      await uploadOfferLetter({
        applicationId,
        candidateId,
        employmentType: offerLetterForm.employmentType.trim(),
        location: offerLetterForm.location.trim(),
        dateOfJoining: offerLetterForm.dateOfJoining,
        workMode: offerLetterForm.workMode,
        offerLetter: selectedFile,
      });

      setSelectedCandidates((currentCandidates) =>
        currentCandidates.filter(
          (candidate) =>
            String(candidate.id) !== String(applicationId),
        ),
      );
      setSelectedFile(null);
      setSelectedFileName("");
      setFileError("");
      setOfferLetterForm({
        candidateId: "",
        fullName: "",
        email: "",
        employmentType: "",
        location: "",
        dateOfJoining: "",
        workMode: "",
      });
      setIsPopupOpen(true);
    } catch (error) {
      setFileError(
        error.response?.data?.message || "Failed to upload offer letter.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenPreview = () => {
    if (!previewUrl) {
      return;
    }

    const previewWindow = window.open(previewUrl, "_blank", "noopener");

    if (!previewWindow) {
      setFileError("Preview was blocked by the browser. Please allow popups and try again.");
    }
  };

  if (activeCandidate) {
    return (
      <article className="hr-page-card offer-letter-page">
        <div className="offer-letter-detail-header">
          <div className="hr-page-title">
            <FileCheck2 size={20} />
            <h2>Releasing Offer Letter</h2>
          </div>
          <button
            type="button"
            className="offer-letter-back-btn"
            onClick={() => {
              setActiveCandidate(null);
              setSelectedFile(null);
              setSelectedFileName("");
              setFileError("");
              setOfferLetterForm({
                candidateId: "",
                fullName: "",
                email: "",
                employmentType: "",
                location: "",
                dateOfJoining: "",
                workMode: "",
              });
            }}
          >
            Back to Candidates
          </button>
        </div>

        <form className="offer-letter-form-card" onSubmit={handleSubmitOfferLetter}>
          <div className="offer-letter-form-grid">
            <div className="offer-letter-form-field">
              <label>Candidate ID</label>
              <input
                type="text"
                value={offerLetterForm.candidateId}
                onChange={handleApplicationIdChange}
                placeholder="Enter candidate ID"
              />
            </div>
            <div className="offer-letter-form-field">
              <label>Candidate Name</label>
              <input type="text" value={offerLetterForm.fullName} readOnly />
            </div>
            <div className="offer-letter-form-field">
              <label>Email</label>
              <input type="email" value={offerLetterForm.email} readOnly />
            </div>
            <div className="offer-letter-form-field">
              <label>Employment Type</label>
              <select
                value={offerLetterForm.employmentType}
                onChange={(event) =>
                  handleOfferLetterFieldChange("employmentType", event.target.value)
                }
                required
              >
                <option value="">Select employment type</option>
                <option value="FULL_TIME">Full Time</option>
                <option value="INTERN">Intern</option>
              </select>
            </div>
            <div className="offer-letter-form-field">
              <label>Location</label>
              <input
                type="text"
                value={offerLetterForm.location}
                onChange={(event) =>
                  handleOfferLetterFieldChange("location", event.target.value)
                }
                placeholder="Enter location"
                required
              />
            </div>
            <div className="offer-letter-form-field">
              <label>Date of Joining</label>
              <input
                type="date"
                value={offerLetterForm.dateOfJoining}
                onChange={(event) =>
                  handleOfferLetterFieldChange("dateOfJoining", event.target.value)
                }
                required
              />
            </div>
            <div className="offer-letter-form-field">
              <label>Work Mode</label>
              <select
                value={offerLetterForm.workMode}
                onChange={(event) =>
                  handleOfferLetterFieldChange("workMode", event.target.value)
                }
                required
              >
                <option value="">Select work mode</option>
                <option value="WFO/WFH/Hybrid">WFO/WFH/Hybrid</option>
              </select>
            </div>
            <div className="offer-letter-form-field offer-letter-upload-field">
              <label>Upload  Offer Letter</label>
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileChange}
                required
              />
              {fileError && (
                <span className="offer-letter-file-error">{fileError}</span>
              )}
              {selectedFileName && (
                <span className="offer-letter-file-row">
                  <span className="offer-letter-file-name">{selectedFileName}</span>
                  <button
                    type="button"
                    className="offer-letter-preview-btn"
                    onClick={handleOpenPreview}
                    disabled={!previewUrl}
                  >
                    <Eye size={16} />
                    Preview
                  </button>
                </span>
              )}
            </div>
          </div>

          <button type="submit" className="offer-letter-submit-btn">
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </form>

        {isPopupOpen && (
          <div
            className="offer-letter-popup-overlay"
            onClick={handleClosePopup}
          >
            <div
              className="offer-letter-popup-card"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Offer letter generated message"
            >
              <h3>Offer Letter Generated</h3>
              <p>Offer letter generated successfully.</p>
              <button
                type="button"
                className="offer-letter-popup-btn"
                onClick={handleClosePopup}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </article>
    );
  }

  return (
    <article className="hr-page-card offer-letter-page">
      <div className="hr-page-title">
        <FileCheck2 size={20} />
        <h2>Releasing Offer Letter</h2>
      </div>

      <div className="offer-letter-toolbar">
        <div className="offer-letter-search">
          <Search size={16} />
          <input
            type="text"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search selected candidate by name, email, role, or location"
            aria-label="Search selected candidates"
          />
        </div>

        <div className="offer-letter-count">
          <strong>{filteredCandidates.length}</strong>
          <span>selected candidate{filteredCandidates.length === 1 ? "" : "s"}</span>
        </div>
      </div>

      <div className="offer-letter-table">
        <div className="offer-letter-head">
          <span>Candidate</span>
          <span>Job</span>
        </div>

        {isLoading ? (
          <div className="offer-letter-empty">Loading selected candidates...</div>
        ) : filteredCandidates.length > 0 ? (
          filteredCandidates.map((candidate) => (
            <div
              className="offer-letter-row offer-letter-row-clickable"
              key={candidate.id}
              onClick={() => handleOpenCandidate(candidate)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleOpenCandidate(candidate);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="offer-letter-candidate">
                <p className="offer-letter-detail">
                  <strong>Name:</strong>{" "}
                  <span className="offer-letter-name-btn">{candidate.fullName}</span>
                </p>
                <p className="offer-letter-detail">
                  <strong>Email:</strong> {candidate.email}
                </p>
                <p className="offer-letter-detail">
                  <strong>Phone:</strong> {candidate.phone}
                </p>
                <p className="offer-letter-detail">
                  <strong>Location:</strong> {candidate.location}
                </p>
              </div>
              <div className="offer-letter-role">
                <strong>{candidate.jobTitle}</strong>
                <p className="offer-letter-date">
                  <strong>Applied On:</strong> {formatAppliedDate(candidate.createdAt)}
                </p>
              </div>
            </div>
          ))
        ) : (
          <div className="offer-letter-empty">
            No selected candidates found for your search.
          </div>
        )}
      </div>
    </article>
  );
};

export default HRReleasingOfferLetterPage;
